import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../contexts/AuthContext';
import { relationshipRecommAPI } from '../services/api';
import ThemeToggle from './ThemeToggle';
import './AppHeader.css';

/**
 * Reusable application header component providing consistent navigation.
 *
 * Props:
 * - showBackButton: boolean - whether to show back button (default: auto-detected from route)
 * - backTo: string - route to navigate back to (default: '/')
 * - backLabel: string - label for back button (default: auto-generated)
 * - title: string - optional custom title (default: 'Family Tree')
 * - children: ReactNode - optional additional content in header actions area
 */
function AppHeader({
  showBackButton,
  backTo = '/',
  backLabel,
  title,
  children
}) {
  const { t } = useTranslation();
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const [recommCount, setRecommCount] = useState(0);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const topLevelRoutes = new Set(['/', '/photos', '/intelligence']);
  const isTopLevelRoute = topLevelRoutes.has(location.pathname);

  // Auto-detect showBackButton if not explicitly set
  const shouldShowBackButton = showBackButton !== undefined ? showBackButton : !isTopLevelRoute;

  // Default back label based on destination
  const defaultBackLabel = backTo === '/' ? t('intelligence.backToTree') : t('common.back');
  const finalBackLabel = backLabel || defaultBackLabel;
  const userDisplayName = user?.full_name || user?.username || t('common.user');
  const avatarLabel = userDisplayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('') || 'U';
  const isTreeTabActive = !location.pathname.startsWith('/photos') && !location.pathname.startsWith('/intelligence');

  // Fetch recommendation count for badge
  useEffect(() => {
    relationshipRecommAPI.getCount()
      .then(res => setRecommCount(res.data.count))
      .catch(() => setRecommCount(0));
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    }

    function handleEscape(event) {
      if (event.key === 'Escape') {
        setIsUserMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  return (
    <header className="app-header">
      <div className="app-header-left">
        {shouldShowBackButton && (
          <Link to={backTo} className="btn btn-secondary btn-back">
            {finalBackLabel}
          </Link>
        )}
        <Link to="/" className="app-header-title">
          {title || t('dashboard.title')}
        </Link>
      </div>

      <nav className="app-header-nav" aria-label="Primary">
        <NavLink to="/" end className={`app-header-tab ${isTreeTabActive ? 'active' : ''}`}>
          {t('tree.treeLayout')}
        </NavLink>
        <NavLink
          to="/photos"
          className={({ isActive }) => `app-header-tab ${isActive ? 'active' : ''}`}
        >
          {t('photos.title', 'Photos')}
        </NavLink>
        <NavLink
          to="/intelligence"
          className={({ isActive }) => `app-header-tab app-header-tab-intelligence ${isActive ? 'active' : ''}`}
        >
          {t('intelligence.title', 'Intelligence')}
          {recommCount > 0 && <span className="badge">{recommCount}</span>}
        </NavLink>
      </nav>

      <div className="app-header-actions">
        {children}
        <div className="app-header-user-menu" ref={userMenuRef}>
          <button
            type="button"
            className="app-header-avatar-button"
            onClick={() => setIsUserMenuOpen(open => !open)}
            aria-expanded={isUserMenuOpen}
            aria-haspopup="menu"
            aria-label={t('common.welcome', { name: userDisplayName })}
          >
            <span className="app-header-avatar">{avatarLabel}</span>
          </button>

          {isUserMenuOpen && (
            <div className="app-header-user-dropdown" role="menu">
              <div className="app-header-user-summary">
                <span className="app-header-user-name">{userDisplayName}</span>
              </div>
              <div className="app-header-user-menu-section">
                <span className="app-header-menu-label">{t('common.theme')}</span>
                <ThemeToggle />
              </div>
              <button type="button" onClick={logout} className="app-header-menu-action" role="menuitem">
                {t('auth.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default AppHeader;
